import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseHttpServiceService } from '../../../sharedFeatures/services/base-http-service.service';
import { CurrentUserService } from '../../../sharedFeatures/services/current-user.service';
import {
  UserModule,
  SelectModuleRequest,
  SelectModuleResponse,
} from '../models/user-module.model';
import { UmRole } from '../../roles/models/role-enum';

@Injectable({
  providedIn: 'root',
})
export class ModuleSelectionService extends BaseHttpServiceService {
  constructor(
    http: HttpClient,
    translateService: TranslateService,
    currentUserService: CurrentUserService
  ) {
    super(http, translateService, currentUserService);
  }

  /**
   * Get all modules accessible by the current user
   */
  getUserModules(): Observable<UserModule[]> {
    let url: string = `${this.mysteryShopperUrl}/User/GetUserModules`;
    return this.getData<UserModule[]>(url).pipe(
      map((modules: UserModule[]) => {
        // Update link for module with id = 2 (Mystery Shopper Module) to point to new Angular version
        const updatedModules = modules.map(module => {
          if (module.id === 2) {
            return {
              ...module,
              link: window.location.origin, // Point to the current Angular app (new version)
            };
          }
          return module;
        });

        // Filter out modules with no roles (roles array is empty or length is 0)
        return updatedModules.filter(
          module => module.roles && module.roles.length > 0
        );
      })
    );
  }

  /**
   * Select a module for internal navigation (same origin)
   * Handles module selection client-side since Angular app doesn't share session with MVC
   */
  selectModule(request: SelectModuleRequest): Observable<SelectModuleResponse> {
    // Handle module selection client-side
    return new Observable(observer => {
      observer.next({
        success: true,
        redirectUrl: this.getModuleRedirectUrl(
          request.moduleId,
          request.roleId
        ),
      });
      observer.complete();
    });
  }

  /**
   * Generate token for external module navigation
   */
  generateToken(moduleId: number, roleId?: number): Observable<string> {
    let url: string = `${this.mysteryShopperUrl}/User/GenerateAndSaveToken?ModuleId=${moduleId}`;
    if (roleId) {
      url += `&roleId=${roleId}`;
    }

    // Use getTextData instead of getData because API returns plain text, not JSON
    return this.getTextData(url);
  }

  /**
   * Get redirect URL based on module and role
   */
  public getModuleRedirectUrl(moduleId: number, roleId?: number): string {
    // Mystery Shopper Module
    if (moduleId === 2) {
      if (roleId === UmRole.ProjectHead) {
        // ProjectHead
        return '/msm-checklist/general-dashboard/divisions';
      } else if (roleId === UmRole.Agent) {
        // Agent
        return '/msm-checklist/agent-ui/dashboard';
      } else if (roleId === UmRole.Stakeholder) {
        // Stakeholder
        return '/msm-checklist/general-dashboard/divisions';
      } else if (roleId === UmRole.ProjectAdmin) {
        // ProjectAdmin
        return '/msm-checklist/project-admin-ui';
      } else if (roleId === UmRole.Dashboard) {
        // Dashboard
        return '/msm-checklist/general-dashboard/divisions';
      } else {
        return '/msm-checklist/action-plan/list';
      }
    }
    // Project Tracker or other modules
    else {
      return '/dashboard';
    }
  }
}
